import React, { useMemo } from "react";
import { isNil } from "ramda";
import { styled } from "styled-components";

import { List } from "affinityUI/data/list";
import { Summary } from "affinityUI/data/summary";
import { DateTime } from "affinityUI/data/valueDisplay";
import { Grid } from "affinityUI/elements/grid";
import { Tag, TagType } from "affinityUI/elements/tag";
import { UnitHaemoglobin, UnitNeutrophil, UnitPlatelet, UnitWBC } from "components/views/patientDetails/ui/resultUnits";
import { Maybe } from "gql";
import { Measurement, PatientsListEntry } from "types/patient";

import { Column } from "./components/column";
import { Result } from "./components/result";
import { WarningTag } from "./components/warningTag";

export enum STATUS {
  Warn = "WARN",
  Alert = "ALERT",
}

const ResultTag = styled(Tag)`
  margin: 0 0 0.25rem 0;
`;

const getStatus = (measurement?: Measurement, confirmatoryTest?: boolean): STATUS | undefined => {
  if (confirmatoryTest) {
    return STATUS.Alert;
  }
  if (measurement?.measurementInterval?.outOfRange) {
    return STATUS.Warn;
  }
};

export const getTag = (
  daysAgo?: Maybe<number>
): {
  timeAgo: string;
  tagType: TagType;
} => {
  if (isNil(daysAgo))
    return {
      timeAgo: "Never tested",
      tagType: TagType.neutral,
    };

  let timeAgo;
  let tagType: TagType;
  if (daysAgo >= 7) {
    // Red - 7 days
    tagType = TagType.negative;
  } else if (daysAgo < 7 && daysAgo >= 2) {
    // Amber - 48 hours to 7 days
    tagType = TagType.warn;
  } else {
    // Green - up to 48 hours
    tagType = TagType.positive;
  }

  if (daysAgo < 1) {
    timeAgo = "Today";
  } else if (daysAgo < 2) {
    timeAgo = "Yesterday";
  } else {
    timeAgo = `${daysAgo} days ago`;
  }

  return {
    timeAgo,
    tagType,
  };
};

export type ResultCardProps = {
  patient: PatientsListEntry;
  normalRanges?: Maybe<NormalRanges>;
  neutrophilThreshold?: Maybe<number>;
};

export const ResultCard: React.FC<ResultCardProps> = ({ neutrophilThreshold, normalRanges, patient }) => {
  const { id, sex, lastName, firstName, dateOfBirth, hospitalNumber, latestResult } = patient;
  const dateRecorded = latestResult ? latestResult.dateRecorded : "-";
  const tag = useMemo(() => (latestResult ? getTag(latestResult.daysAgo) : null), [latestResult]);
  const href = `${patient.id}`;

  const meta = useMemo(
    () => (
      <>
        {dateOfBirth && (
          <Summary.Item label="DOB:">
            <DateTime value={dateOfBirth} format={DateTime.FORMAT.DATE} />
          </Summary.Item>
        )}
        <Summary.Item label="HOSP. no:">{hospitalNumber}</Summary.Item>
        <Summary.Item label="Patient ID:">{id}</Summary.Item>
      </>
    ),
    [dateOfBirth, hospitalNumber, id]
  );
  const confirmatoryTest = Boolean(latestResult?.belowNeutrophilThreshold);

  const normalHaemoglobinRange =
    sex === "Female" ? normalRanges?.haemoglobinRangeFemale : normalRanges?.haemoglobinRangeMale;

  return (
    <List.Item
      data-testid="patient-result-card"
      title={`${lastName}, ${firstName}`}
      meta={meta}
      tags={<WarningTag patient={patient} neutrophilThreshold={neutrophilThreshold} />}
      aria-label={`View ${patient.lastName}, ${patient.firstName}`}
      key={patient.id}
      linkTo={href}
    >
      <Grid $tight $cols={[Grid.cols.EQUAL]}>
        {latestResult && tag !== null ? (
          <Column>
            <ResultTag type={tag.tagType} data-testid="result-when">
              {tag.timeAgo}
            </ResultTag>
            <div aria-label="time">
              <DateTime format={DateTime.FORMAT.TIME} value={dateRecorded} />
            </div>
            <div aria-label="date">
              <DateTime format={DateTime.FORMAT.DATE} value={dateRecorded} />
            </div>
          </Column>
        ) : (
          <Column>
            <ResultTag type={TagType.neutral} data-testid="result-when">
              Never tested
            </ResultTag>
            <div>Awaiting first result</div>
          </Column>
        )}
        <Result
          value={latestResult?.whiteBloodCells.value}
          label="WBC"
          status={getStatus(latestResult?.whiteBloodCells)}
          unit={<UnitWBC normalRange={normalRanges?.wbcRange} />}
          id={`wbc-${id}`}
        />
        <Result
          value={latestResult?.neutrophils.value}
          label="Neutrophils"
          status={getStatus(latestResult?.neutrophils, confirmatoryTest)}
          unit={<UnitNeutrophil normalRange={normalRanges?.neutrophilRange} />}
          id={`neutrophils-${id}`}
        />
        <Result
          value={latestResult?.haemoglobin.value}
          label="Haemoglobin"
          status={getStatus(latestResult?.haemoglobin)}
          unit={<UnitHaemoglobin normalRange={normalHaemoglobinRange} />}
          id={`haemoglobin-${id}`}
        />
        <Result
          value={latestResult?.platelets.value}
          label="Platelets"
          status={getStatus(latestResult?.platelets)}
          unit={<UnitPlatelet normalRange={normalRanges?.plateletRange} />}
          id={`platelets-${id}`}
        />
      </Grid>
    </List.Item>
  );
};
